import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { authFetch } from '../utils/authUtils';

const PaymentCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, failed
  const [message, setMessage] = useState('Verifying your payment...');
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    verifyPayment();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyPayment = async () => {
    // PayPal returns ?token=<orderId>&PayerID=<id>
    // Paystack returns ?reference=<ref>&trxref=<ref>
    // We add ?purpose=vault on vault-access checkouts so we know which capture
    // endpoint to call.
    const purpose = searchParams.get('purpose');
    const paypalOrderId = searchParams.get('token');
    const paypalPayerId = searchParams.get('PayerID');
    const paystackRef = searchParams.get('reference') || searchParams.get('trxref');

    // --- Vault Access (PayPal one-time) ---
    if (purpose === 'vault' && paypalOrderId && paypalPayerId) {
      try {
        const response = await authFetch(
          `${BACKEND_URL}/api/vault/buy/paypal/capture/${paypalOrderId}`,
          { method: 'POST' },
        );
        const data = await response.json();
        if (response.ok && (data.status === 'success' || data.status === 'already_paid')) {
          setStatus('success');
          setMessage(
            data.message ||
              'Vault payment received! An admin will issue your private code shortly.',
          );
          setTimeout(() => {
            window.location.href = '/vault';
          }, 4000);
        } else {
          setStatus('failed');
          setMessage(
            (typeof data.detail === 'string' && data.detail) ||
              data.message ||
              'Vault payment did not complete',
          );
        }
      } catch (error) {
        console.error('Vault PayPal verification error:', error);
        setStatus('failed');
        setMessage('Failed to verify Vault payment. Please contact support.');
      }
      return;
    }

    // --- PayPal flow (subscription) ---
    if (paypalOrderId && paypalPayerId) {
      try {
        const response = await authFetch(
          `${BACKEND_URL}/api/subscription/paypal/capture/${paypalOrderId}`,
          { method: 'POST' },
        );
        const data = await response.json();
        if (response.ok && data.status === 'success') {
          setStatus('success');
          setMessage(
            `Payment successful! Your Premium subscription is now active until ${new Date(data.expires_at).toLocaleDateString()}.`,
          );
          setTimeout(() => {
            window.location.href = '/dashboard';
          }, 3000);
        } else {
          setStatus('failed');
          setMessage(
            (typeof data.detail === 'string' && data.detail) ||
              data.message ||
              'PayPal payment did not complete',
          );
        }
      } catch (error) {
        console.error('PayPal verification error:', error);
        setStatus('failed');
        setMessage('Failed to verify PayPal payment. Please contact support.');
      }
      return;
    }

    // --- Paystack flow: verify the reference ---
    if (!paystackRef) {
      setStatus('failed');
      setMessage('No payment reference found');
      return;
    }

    try {
      const response = await authFetch(
        `${BACKEND_URL}/api/subscription/paystack/verify/${paystackRef}`,
      );
      const data = await response.json();

      if (data.status === 'success') {
        setStatus('success');
        setMessage(
          `Payment successful! Your Premium subscription is now active until ${new Date(data.expires_at).toLocaleDateString()}.`,
        );
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 3000);
      } else {
        setStatus('failed');
        setMessage(data.message || 'Payment verification failed');
      }
    } catch (error) {
      console.error('Payment verification error:', error);
      setStatus('failed');
      setMessage('Failed to verify payment. Please contact support.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-slate-800/50 border border-slate-700 rounded-2xl p-8 text-center">
        {status === 'verifying' && (
          <>
            <Loader2 className="w-16 h-16 text-indigo-400 animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-white mb-2">Verifying Payment</h2>
            <p className="text-gray-400">{message}</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Successful!</h2>
            <p className="text-gray-300 mb-4">{message}</p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <XCircle className="w-10 h-10 text-red-400" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Payment Failed</h2>
            <p className="text-gray-300 mb-6">{message}</p>
            <div className="space-y-3">
              <button
                onClick={() => navigate('/premium')}
                className="w-full px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-semibold"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate('/dashboard')}
                className="w-full px-4 py-2 bg-transparent border border-slate-600 hover:bg-slate-700 text-white rounded-lg"
              >
                Back to Dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default PaymentCallback;
